// Two flows copy a project into a new one (clone: live head; restore: one trail version). Each
// project records only ONE hop; nesting `sourceProject` in the query and flattening it here rebuilds the chain.

/** Nesting depth of `sourceProject` in GetProjectVersionsQuery — the two must move together, or the chain overreads ancestors nobody renders or hides ones that were fetched; a chain longer than this reports truncated rather than silently stopping. */
export const PROJECT_LINEAGE_MAX_STEPS = 5;

/** One hop of the chain: a project this one descends from, and the state that was copied out of it. */
export interface ProjectLineageStep {
  /** The ancestor — what this hop's link opens. */
  projectId: string;
  /** The ancestor's name, as Hasura resolved it. */
  projectName: string;
  /** Set when the ancestor is soft-deleted; the hop is then named as plain text rather than a link. A hard-deleted ancestor
   * never gets here: its FK is ON DELETE set null, so it vanishes like an out-of-membership one; only a soft delete still resolves. */
  deletedAt: string | null;
  /** The trail row that was copied out of the ancestor, when this hop was a restore. Null on a clone — and on a restore whose version row no longer exists. */
  versionId: string | null;
  /** The copied version's number. Null means clone: the copy was taken from the ancestor's live head. */
  versionNumber: number | null;
}

/** The origin of a copied project, as the page needs it. Only ever built out of ancestors the reader may see. */
export interface ProjectLineage {
  /** The chain, NEAREST FIRST. Never empty — a lineage with no hop to show is `null` instead. */
  steps: ProjectLineageStep[];
  /** True when the chain continues past the last step read (nesting ran out) — never when an ancestor is merely hidden from the reader, which ends the chain in silence instead. */
  truncated: boolean;
}

/** Typed loosely like {@link import("./versionMapping").ProjectVersionRowLike}: every field is guarded, not trusted, so a mid-rollout environment or a hidden ancestor maps to a shorter chain rather than a half-built row. Recursive, mirroring the query's nesting. */
export interface ProjectLineageRowLike {
  sourceProjectId?: string | null;
  sourceVersionId?: string | null;
  sourceVersionNumber?: number | null;
  sourceProject?: ProjectLineageAncestorLike | null;
}

/** An ancestor: its identity, plus the origin columns describing how IT was copied — the next hop up. */
export interface ProjectLineageAncestorLike extends ProjectLineageRowLike {
  id?: string | null;
  name?: string | null;
  deletedAt?: string | null;
}

/** The stand-in for an ancestor Hasura resolved without a usable name. `project.name` is NOT NULL, so this is a render guard, not a state. */
const UNKNOWN_PROJECT_NAME = "Unknown project";

/** Number.isFinite, not a null check: 0 is a real version number, so "absent" can't be told from "zero" by truthiness. Anything not finite reads as a clone — the honest fallback, unlike "(vNaN)". */
const finiteVersionNumber = (value: number | null | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

// A hop is dropped whenever `sourceProject` resolves null, even with `sourceProjectId` set — never
// fall back to the bare id (see versioning.md: a membership-nulled relationship can still leak the id).
export const mapProjectLineage = (row: ProjectLineageRowLike | null | undefined): ProjectLineage | null => {
  const steps: ProjectLineageStep[] = [];
  const seen = new Set<string>();
  let descendant: ProjectLineageRowLike | null = row ?? null;

  while (descendant) {
    // Stopped by the document's nesting depth rather than by the data: the deepest ancestor read
    // still names a project it came from, so say the chain continues instead of ending on it.
    if (steps.length === PROJECT_LINEAGE_MAX_STEPS) return { steps, truncated: Boolean(descendant.sourceProjectId) };

    const ancestor = descendant.sourceProjectId && descendant.sourceProject ? descendant.sourceProject : null;
    if (!ancestor) break;

    // The relationship's own id, falling back to the FK the guard above already proved is set — the
    // two are the same row, and the fallback only matters to an environment that selected one.
    const projectId = ancestor.id || descendant.sourceProjectId || "";
    // A copy is always younger than what it was copied from, so a cycle cannot exist. Guarded
    // anyway: a repeat would otherwise loop the same names into the row and duplicate its keys.
    if (!projectId || seen.has(projectId)) break;
    seen.add(projectId);

    steps.push({
      projectId,
      projectName: ancestor.name || UNKNOWN_PROJECT_NAME,
      deletedAt: ancestor.deletedAt ?? null,
      // The version columns live on the DESCENDANT: they record which state of the ancestor was
      // copied out of it, so each hop reads them from the row below it, not from the ancestor.
      versionId: descendant.sourceVersionId ?? null,
      versionNumber: finiteVersionNumber(descendant.sourceVersionNumber),
    });

    descendant = ancestor;
  }

  return steps.length > 0 ? { steps, truncated: false } : null;
};

/** One hop's label, split where the link starts (the row renders `target` as a RouterLink); kept in sync with {@link projectLineageLabel}. */
export interface ProjectLineageStepLabel {
  /** What was done, immediately before the link — `Cloned from`, or `— itself restored from` further up the chain. */
  action: string;
  /** The whole link text — quoted name, or name + version on a restore. The version marker is INSIDE the link since the link opens that version. */
  target: string;
}

/** Surrounding spaces live in the STRING, not the template: Vue drops a v-for text node's leading whitespace, so a template-side space would glue this to the previous hop's link. */
const CHAIN_SEPARATOR = " — ";

/** Opens the sentence. */
const FIRST_HOP_ACTIONS = { clone: "Cloned from", restore: "Restored from" };

/** Continues it, one hop further up the chain. */
const CHAIN_HOP_ACTIONS = {
  clone: `${CHAIN_SEPARATOR}itself cloned from`,
  restore: `${CHAIN_SEPARATOR}itself restored from`,
};

/** Said once at the end of a chain that goes back further than the row shows. */
export const PROJECT_LINEAGE_TRUNCATED_LABEL = `${CHAIN_SEPARATOR}earlier copies not shown`;

/** Which copy kind is told by the version number alone: a clone has none (taken from the live head, not a trail point). `index` picks opening vs continuing wording. */
export const projectLineageStepLabel = (step: ProjectLineageStep, index: number): ProjectLineageStepLabel => {
  const actions = index === 0 ? FIRST_HOP_ACTIONS : CHAIN_HOP_ACTIONS;
  const named = `“${step.projectName}”`;
  return step.versionNumber === null
    ? { action: actions.clone, target: named }
    : { action: actions.restore, target: `${named} (v${step.versionNumber})` };
};

/** Joined with nothing between hops — each continuation carries its own separator, matching the template's discipline, so this string and the rendered row can't drift apart. */
export const projectLineageLabel = (lineage: ProjectLineage): string => {
  const sentence = lineage.steps
    .map((step, index) => {
      const { action, target } = projectLineageStepLabel(step, index);
      return `${action} ${target}`;
    })
    .join("");
  return lineage.truncated ? `${sentence}${PROJECT_LINEAGE_TRUNCATED_LABEL}` : sentence;
};
